import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { call, clearToken, getToken, setToken, UNAUTHORIZED_EVENT } from './api';
import { useConfigMaybe } from './config';

/** The `/account/me` answer, as much of it as the site reads. */
export interface Account {
  accountId: number;
  username: string;
  displayName?: string;
  profileImage?: string | null;
  bannerImage?: string | null;
  email?: string | null;
  availableUsernameChanges?: number;
  createdAt?: string;
  [k: string]: unknown;
}

export interface Roles {
  staff: boolean;
  developer: boolean;
}

/** Decode the token's payload without verifying it (cosmetic role gate, §1.3). */
export function rolesFromToken(token: string | null): Roles {
  const none = { staff: false, developer: false };
  if (!token) return none;
  const part = token.split('.')[1];
  if (!part) return none;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(padded);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (!payload || typeof payload !== 'object') return none;
    const raw = (payload as Record<string, unknown>).role;
    const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
    const roles = list.filter((r): r is string => typeof r === 'string');
    const developer = roles.includes('developer');
    return { developer, staff: developer || roles.includes('moderator') };
  } catch {
    return none;
  }
}

export type Session =
  | { status: 'unknown' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; token: string; account: Account; roles: Roles };

interface SessionApi {
  session: Session;
  /** store a fresh token and load its account; rejects (and stays signed out) if `/me` fails */
  signIn(token: string): Promise<Account>;
  signOut(): void;
  /** re-read `/account/me` */
  refresh(): Promise<void>;
  /** patch the account in place (after a successful edit) */
  updateAccount(patch: Partial<Account>): void;
}

const SessionContext = createContext<SessionApi | null>(null);

function isAccount(v: unknown): v is Account {
  if (!v || typeof v !== 'object') return false;
  const a = v as Record<string, unknown>;
  return typeof a.accountId === 'number' && a.accountId > 0 && typeof a.username === 'string';
}

export function accountName(a: { displayName?: string | null; username?: string | null }) {
  return a.displayName || a.username || '';
}

export function SessionProvider({ children, initial }: { children: ReactNode; initial?: Session }) {
  const config = useConfigMaybe();
  const [session, setSession] = useState<Session>(initial ?? { status: 'unknown' });

  const loadMe = useCallback(
    async (token: string) => {
      if (!config || config.failed) throw new Error('The server is unreachable right now.');
      const me = await call<Account>(`${config.hosts.accounts}/account/me`, { auth: token });
      // Only a real account counts as a session (§1.2): an answer without one means the token
      // isn't usable here (e.g. one left over from the old server), so drop it.
      if (!isAccount(me)) {
        clearToken();
        throw new Error('Your session has expired. Please sign in again.');
      }
      return me;
    },
    [config],
  );

  // Startup (§1.2): once the config is known, check a stored token.
  useEffect(() => {
    if (initial || !config) return;
    const token = getToken();
    if (!token || config.failed) {
      setSession({ status: 'signedOut' });
      return;
    }
    let live = true;
    loadMe(token)
      .then((account) => {
        if (live) setSession({ status: 'signedIn', token, account, roles: rolesFromToken(token) });
      })
      .catch(() => {
        if (live) setSession({ status: 'signedOut' });
      });
    return () => {
      live = false;
    };
  }, [config, initial, loadMe]);

  useEffect(() => {
    const onUnauthorized = () => setSession({ status: 'signedOut' });
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const api = useMemo<SessionApi>(
    () => ({
      session,
      async signIn(token) {
        setToken(token);
        try {
          const account = await loadMe(token);
          setSession({ status: 'signedIn', token, account, roles: rolesFromToken(token) });
          return account;
        } catch (e) {
          clearToken();
          setSession({ status: 'signedOut' });
          throw e;
        }
      },
      signOut() {
        clearToken();
        setSession({ status: 'signedOut' });
      },
      async refresh() {
        if (session.status !== 'signedIn') return;
        const account = await loadMe(session.token);
        setSession((s) => (s.status === 'signedIn' ? { ...s, account } : s));
      },
      updateAccount(patch) {
        setSession((s) =>
          s.status === 'signedIn' ? { ...s, account: { ...s.account, ...patch } } : s,
        );
      },
    }),
    [session, loadMe],
  );

  return <SessionContext.Provider value={api}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}

/** The signed-in session, for components only rendered while signed in. */
export function useSignedIn() {
  const { session, ...rest } = useSession();
  if (session.status !== 'signedIn') throw new Error('useSignedIn while not signed in');
  return { ...session, ...rest };
}
